import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, MessageCircle, Users, Award, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { track1Data } from '../mock';

const Track1 = () => {
  const iconMap = {
    'book-open': BookOpen,
    'message-circle': MessageCircle,
    'users': Users,
    'award': Award,
    'heart': Heart
  };

  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-[#0A277A] to-[#3F54C8] text-white">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold uppercase mb-6">
              {track1Data.subtitle}
            </div>
            <h1 className="font-black text-5xl md:text-8xl leading-[0.9] uppercase mb-6">
              Track 1
            </h1>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Personal Mastery & Development
            </h2>
            <p className="text-white/90 text-sm md:text-base uppercase tracking-wider mb-8">
              {track1Data.tagline}
            </p>
            <p className="text-white/95 text-lg md:text-xl leading-relaxed mb-10">
              {track1Data.description}
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#0A277A] hover:bg-gray-100 rounded-full px-8 py-6 text-base font-semibold uppercase transition-all hover:scale-105 shadow-lg">
                Get Involved
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-4">
              Our Programs
            </h2>
            <p className="text-gray-600 text-lg">
              Building confidence and clarity through guided reading, reflection, and conversation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {track1Data.programs.map((program) => {
              const Icon = iconMap[program.icon];
              return (
                <Card key={program.id} className="bg-gray-50 border-2 border-gray-200 p-8 hover:border-[#0A277A] hover:shadow-lg transition-all duration-300">
                  <div className="bg-[#0A277A] text-white w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <Icon size={32} />
                  </div>
                  <h3 className="font-black text-2xl text-[#0A277A] uppercase mb-4">
                    {program.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {program.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured: MOS Book Box */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#0A277A] text-white px-4 py-2 rounded-full text-sm font-bold uppercase mb-6">
                Featured Program
              </div>
              <h2 className="font-black text-4xl md:text-5xl text-[#0A277A] uppercase mb-6">
                MOS Book Box
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Our signature program delivers carefully curated book bundles designed to spark reflection, growth, and purpose discovery. Each box includes:
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start text-gray-700">
                  <div className="bg-[#0A277A] rounded-full w-2 h-2 mt-2 mr-3 flex-shrink-0"></div>
                  <span>Hand-selected books focused on mindset, leadership, and personal growth</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <div className="bg-[#0A277A] rounded-full w-2 h-2 mt-2 mr-3 flex-shrink-0"></div>
                  <span>Custom reflection journals and writing tools</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <div className="bg-[#0A277A] rounded-full w-2 h-2 mt-2 mr-3 flex-shrink-0"></div>
                  <span>M.O.S. branded items and motivational materials</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <div className="bg-[#0A277A] rounded-full w-2 h-2 mt-2 mr-3 flex-shrink-0"></div>
                  <span>Access to exclusive virtual discussion groups</span>
                </li>
              </ul>
              <Link to="/contact">
                <Button className="bg-[#D80000] text-white hover:bg-[#b00000] rounded-full px-8 py-4 font-semibold uppercase transition-all">
                  Request Information
                </Button>
              </Link>
            </div>
            <div>
              <img
                src={track1Data.image}
                alt="MOS Book Box"
                className="w-full rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MOS Circle */}
      <section className="py-20 bg-[#0A277A] text-white">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 text-center">
          <Heart className="mx-auto mb-6" size={64} />
          <h2 className="font-black text-4xl md:text-5xl uppercase mb-6">
            Join the MOS Circle
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            A monthly community of givers and sponsors who fund book boxes and support growth programs. Receive exclusive access to workshops and peer discussions.
          </p>
          <Link to="/donate">
            <Button className="bg-[#D80000] text-white hover:bg-[#b00000] rounded-full px-10 py-6 text-lg font-semibold uppercase transition-all hover:scale-105 shadow-lg">
              Become a Sponsor
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-black text-3xl md:text-5xl text-[#0A277A] uppercase mb-6">
            Start Your Journey
          </h2>
          <p className="text-gray-700 text-lg mb-8">
            Transform your mindset and discover your purpose through the power of reading and reflection.
          </p>
          <Link to="/contact">
            <Button className="bg-[#0A277A] text-white hover:opacity-90 rounded-full px-10 py-6 text-lg font-semibold uppercase transition-all">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Track1;